import React, { useState } from "react";
import {
    useGetAdminSafeStatisticsQuery,
    useGetAdminSafeUsersQuery,
    useGetAdminSafeUserDetailsQuery,
    useUpdateStructuralTopupPaidMutation,
} from "../api";
import {
    SafeUser,
    SafeProgramDetails,
    SafeProgramStatistics,
    SafeStatistics,
} from "../api/safe";

const SafeStatisticsPage: React.FC = () => {
    // Состояния для пагинации и фильтров
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage] = useState(25);
    const [sortBy, setSortBy] = useState<string>("id");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [search, setSearch] = useState("");
    const [programIdFilter, setProgramIdFilter] = useState<number | undefined>(undefined);
    const [structuralTopupPaidFilter, setStructuralTopupPaidFilter] = useState<boolean | undefined>(undefined);
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

    // Запросы данных
    const { data: statisticsData } = useGetAdminSafeStatisticsQuery();

    const { data: usersData, isLoading: isLoadingUsers } =
        useGetAdminSafeUsersQuery({
            page: currentPage,
            per_page: perPage,
            sort_by: sortBy,
            sort_order: sortOrder,
            search: search || undefined,
            program_id: programIdFilter,
            structural_topup_paid: structuralTopupPaidFilter,
        });

    const { data: userDetailsData, isLoading: isLoadingUserDetails } =
        useGetAdminSafeUserDetailsQuery(selectedUserId!, {
            skip: !selectedUserId,
        });

    const [updateStructuralTopupPaid] = useUpdateStructuralTopupPaidMutation();

    // Статистические данные
    const statistics: SafeStatistics = statisticsData?.data || {
        total_all_users: 0,
        total_users_with_safe_programs: 0,
        total_users: 0,
        total_money_safe: "0.00",
        total_money_safe_work: "0.00",
        programs_statistics: [],
    };

    const users = usersData?.data?.users || [];
    const pagination = usersData?.data?.pagination || {
        current_page: 1,
        last_page: 1,
        per_page: 25,
        total: 0,
        from: null,
        to: null,
    };

    const userDetails = userDetailsData?.data;

    // Обработчики
    const handleSort = (field: string) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortOrder("asc");
        }
        setCurrentPage(1);
    };

    const handleSearch = (value: string) => {
        setSearch(value);
        setCurrentPage(1);
    };

    const handleProgramFilterChange = (value: string) => {
        setProgramIdFilter(value === "" ? undefined : parseInt(value, 10));
        setCurrentPage(1);
    };

    const handleStructuralTopupFilterChange = (value: string) => {
        if (value === "") {
            setStructuralTopupPaidFilter(undefined);
        } else {
            setStructuralTopupPaidFilter(value === "true");
        }
        setCurrentPage(1);
    };

    const handleShowDetails = (userId: number) => {
        setSelectedUserId(userId);
    };

    const closeModal = () => {
        setSelectedUserId(null);
    };

    const formatCurrency = (amount: string) => {
        return parseFloat(amount || "0").toLocaleString("ru-RU", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const getStatusBadge = (isActive: boolean) => {
        return isActive ? (
            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                Активна
            </span>
        ) : (
            <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                Неактивна
            </span>
        );
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-8 text-gray-900 dark:text-gray-200">
                Статистика программы Сейф
            </h1>

            {/* Статистические карточки */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <div className="flex items-center">
                        <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                            👥
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                Всего пользователей / Активировали Сейф
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {statistics.total_all_users.toLocaleString()}/
                                {statistics.total_users_with_safe_programs.toLocaleString()}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <div className="flex items-center">
                        <div className="p-3 rounded-full bg-green-100 text-green-600">
                            💰
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                Свободные средства
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                ${formatCurrency(statistics.total_money_safe)}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <div className="flex items-center">
                        <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
                            🔒
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                Заблокированные средства
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                $
                                {formatCurrency(
                                    statistics.total_money_safe_work
                                )}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Статистика по программам */}
            {statistics.programs_statistics &&
                statistics.programs_statistics.length > 0 && (
                    <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-6">
                            Статистика по программам
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {statistics.programs_statistics.map(
                                (program: SafeProgramStatistics) => {
                                    const getProgramIcon = (type: string) => {
                                        switch (type) {
                                            case "structural":
                                                return "🏗️";
                                            case "charity":
                                                return "💝";
                                            case "star":
                                                return "⭐";
                                            case "marathon":
                                                return "🏃";
                                            default:
                                                return "📊";
                                        }
                                    };

                                    const getProgramColor = (type: string) => {
                                        switch (type) {
                                            case "structural":
                                                return "bg-purple-100 text-purple-600";
                                            case "charity":
                                                return "bg-red-100 text-red-600";
                                            case "star":
                                                return "bg-yellow-100 text-yellow-600";
                                            case "marathon":
                                                return "bg-orange-100 text-orange-600";
                                            default:
                                                return "bg-gray-100 text-gray-600";
                                        }
                                    };

                                    return (
                                        <div
                                            key={program.program_id}
                                            className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
                                        >
                                            <div className="flex items-center">
                                                <div
                                                    className={`p-3 rounded-full ${getProgramColor(
                                                        program.program_type
                                                    )}`}
                                                >
                                                    {getProgramIcon(
                                                        program.program_type
                                                    )}
                                                </div>
                                                <div className="ml-4 flex-1">
                                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                                        {program.program_name}
                                                    </p>
                                                    <div className="mt-2">
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            Общая сумма
                                                        </p>
                                                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                                                            $
                                                            {formatCurrency(
                                                                program.total_amount
                                                            )}
                                                        </p>
                                                    </div>
                                                    <div className="mt-1">
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            К выплате дивидендов
                                                        </p>
                                                        <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                                                            $
                                                            {formatCurrency(
                                                                program.total_dividends_to_pay
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                            )}
                        </div>
                    </div>
                )}

            {/* Фильтры и поиск */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Поиск по имени или email
                        </label>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => handleSearch(e.target.value)}
                            placeholder="ID, имя, email или secret_cookie..."
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Программа
                        </label>
                        <select
                            value={programIdFilter || ""}
                            onChange={(e) => handleProgramFilterChange(e.target.value)}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">Все программы</option>
                            {statistics.programs_statistics.map((program) => (
                                <option key={program.program_id} value={program.program_id}>
                                    {program.program_name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Доплата за структурную
                        </label>
                        <select
                            value={structuralTopupPaidFilter === undefined ? "" : structuralTopupPaidFilter.toString()}
                            onChange={(e) => handleStructuralTopupFilterChange(e.target.value)}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">Все</option>
                            <option value="true">Доплачено</option>
                            <option value="false">Не доплачено</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Таблица пользователей */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-200">
                        Пользователи
                    </h2>
                </div>

                {isLoadingUsers ? (
                    <div className="p-8 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-2 text-gray-600 dark:text-gray-400">
                            Загрузка...
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                            onClick={() => handleSort("id")}
                                        >
                                            ID
                                            {sortBy === "id" && (
                                                <span className="ml-1">
                                                    {sortOrder === "asc"
                                                        ? "↑"
                                                        : "↓"}
                                                </span>
                                            )}
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                            onClick={() => handleSort("name")}
                                        >
                                            ФИО
                                            {sortBy === "name" && (
                                                <span className="ml-1">
                                                    {sortOrder === "asc"
                                                        ? "↑"
                                                        : "↓"}
                                                </span>
                                            )}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Telegram ID
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                            onClick={() => handleSort("email")}
                                        >
                                            Email
                                            {sortBy === "email" && (
                                                <span className="ml-1">
                                                    {sortOrder === "asc"
                                                        ? "↑"
                                                        : "↓"}
                                                </span>
                                            )}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Ценовая группа
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                            onClick={() =>
                                                handleSort("money_safe")
                                            }
                                        >
                                            Свободно
                                            {sortBy === "money_safe" && (
                                                <span className="ml-1">
                                                    {sortOrder === "asc"
                                                        ? "↑"
                                                        : "↓"}
                                                </span>
                                            )}
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                            onClick={() =>
                                                handleSort("money_safe_work")
                                            }
                                        >
                                            Заблокировано
                                            {sortBy === "money_safe_work" && (
                                                <span className="ml-1">
                                                    {sortOrder === "asc"
                                                        ? "↑"
                                                        : "↓"}
                                                </span>
                                            )}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Активные программы
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Действия
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {users.map((user: SafeUser) => (
                                        <tr
                                            key={user.id}
                                            className="hover:bg-gray-50 dark:hover:bg-gray-700"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {user.id}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {user.name} {user.surname}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {user.telegram_id || "—"}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {user.email}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {user.safe_price_group || "-"}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                $
                                                {formatCurrency(
                                                    user.money_safe
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                $
                                                {formatCurrency(
                                                    user.money_safe_work
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {user.active_programs_count}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                <button
                                                    onClick={() =>
                                                        handleShowDetails(
                                                            user.id
                                                        )
                                                    }
                                                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                                >
                                                    Подробнее
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Пагинация */}
                        <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <button
                                    onClick={() =>
                                        setCurrentPage(
                                            Math.max(1, currentPage - 1)
                                        )
                                    }
                                    disabled={currentPage === 1}
                                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Предыдущая
                                </button>
                                <button
                                    onClick={() =>
                                        setCurrentPage(
                                            Math.min(
                                                pagination.last_page,
                                                currentPage + 1
                                            )
                                        )
                                    }
                                    disabled={
                                        currentPage === pagination.last_page
                                    }
                                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Следующая
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700 dark:text-gray-300">
                                        Показано{" "}
                                        <span className="font-medium">
                                            {pagination.from || 0}
                                        </span>{" "}
                                        до{" "}
                                        <span className="font-medium">
                                            {pagination.to || 0}
                                        </span>{" "}
                                        из{" "}
                                        <span className="font-medium">
                                            {pagination.total}
                                        </span>{" "}
                                        результатов
                                    </p>
                                </div>
                                <div>
                                    <nav
                                        className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                                        aria-label="Пагинация"
                                    >
                                        <button
                                            onClick={() =>
                                                setCurrentPage(
                                                    Math.max(1, currentPage - 1)
                                                )
                                            }
                                            disabled={currentPage === 1}
                                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Предыдущая
                                        </button>
                                        {Array.from(
                                            {
                                                length: Math.min(
                                                    5,
                                                    pagination.last_page
                                                ),
                                            },
                                            (_, i) => {
                                                const page = i + 1;
                                                return (
                                                    <button
                                                        key={page}
                                                        onClick={() =>
                                                            setCurrentPage(page)
                                                        }
                                                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                            currentPage === page
                                                                ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                                                : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                                                        }`}
                                                    >
                                                        {page}
                                                    </button>
                                                );
                                            }
                                        )}
                                        <button
                                            onClick={() =>
                                                setCurrentPage(
                                                    Math.min(
                                                        pagination.last_page,
                                                        currentPage + 1
                                                    )
                                                )
                                            }
                                            disabled={
                                                currentPage ===
                                                pagination.last_page
                                            }
                                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Следующая
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Модальное окно с деталями пользователя */}
            {selectedUserId && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-7xl shadow-lg rounded-md bg-white dark:bg-gray-800">
                        <div className="mt-3">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                    Детали пользователя
                                </h3>
                                <button
                                    onClick={closeModal}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <span className="sr-only">Закрыть</span>
                                    <svg
                                        className="h-6 w-6"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>

                            {isLoadingUserDetails ? (
                                <div className="flex justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                </div>
                            ) : userDetails ? (
                                <>
                                    {/* Информация о пользователе */}
                                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                                        <h4 className="text-md font-semibold mb-3 text-gray-900 dark:text-white">
                                            Информация о пользователе
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <span className="font-medium text-gray-600 dark:text-gray-400">
                                                    ID:
                                                </span>
                                                <span className="ml-2 text-gray-900 dark:text-white">
                                                    {userDetails.user.id}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="font-medium text-gray-600 dark:text-gray-400">
                                                    ФИО:
                                                </span>
                                                <span className="ml-2 text-gray-900 dark:text-white">
                                                    {userDetails.user.name}{" "}
                                                    {userDetails.user.surname}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="font-medium text-gray-600 dark:text-gray-400">
                                                    Email:
                                                </span>
                                                <span className="ml-2 text-gray-900 dark:text-white">
                                                    {userDetails.user.email}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="font-medium text-gray-600 dark:text-gray-400">
                                                    Реф.ссылка:
                                                </span>
                                                <span className="ml-2 text-gray-900 dark:text-white">
                                                    {userDetails.user.ref_link}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="font-medium text-gray-600 dark:text-gray-400">
                                                    Ценовая группа:
                                                </span>
                                                <span className="ml-2 text-gray-900 dark:text-white">
                                                    {userDetails.user
                                                        .safe_price_group ||
                                                        "-"}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="font-medium text-gray-600 dark:text-gray-400">
                                                    Свободные средства:
                                                </span>
                                                <span className="ml-2 text-gray-900 dark:text-white">
                                                    $
                                                    {formatCurrency(
                                                        userDetails.user
                                                            .money_safe
                                                    )}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="font-medium text-gray-600 dark:text-gray-400">
                                                    Заблокированные:
                                                </span>
                                                <span className="ml-2 text-gray-900 dark:text-white">
                                                    $
                                                    {formatCurrency(
                                                        userDetails.user
                                                            .money_safe_work
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Таблица программ */}
                                    <div>
                                        <h4 className="text-md font-semibold mb-3 text-gray-900 dark:text-white">
                                            Программы пользователя
                                        </h4>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                                <thead className="bg-gray-50 dark:bg-gray-700">
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                            Программа
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                            Тип
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                            Сумма
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                            Активации
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                            Статус
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                            Цена активации
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                            Дивиденды
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                            Последний платеж
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                            Следующий платеж
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                            Доплата выполнена
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                                    {userDetails.programs.map(
                                                        (
                                                            program: SafeProgramDetails
                                                        ) => (
                                                            <tr
                                                                key={program.id}
                                                            >
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                    {
                                                                        program
                                                                            .program
                                                                            .name
                                                                    }
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                    {
                                                                        program
                                                                            .program
                                                                            .type
                                                                    }
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                    $
                                                                    {formatCurrency(
                                                                        program.amount
                                                                    )}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                    {
                                                                        <div className="flex flex-col space-y-1">
                                                                            <div className="font-bold">Всего: {program.is_active}</div>
                                                                            {program.activations && program.activations.length > 0 && (
                                                                                <div className="mt-2 space-y-2 border-t border-gray-100 dark:border-gray-600 pt-2">
                                                                                    {program.activations.map((activation) => (
                                                                                        <div key={activation.id} className="text-xs flex flex-col bg-gray-50 dark:bg-gray-700/50 p-1.5 rounded">
                                                                                            <span className="font-bold text-blue-600 dark:text-blue-400">
                                                                                                ${formatCurrency(activation.price)}
                                                                                            </span>
                                                                                            <div className="text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5">
                                                                                                <span>
                                                                                                    {activation.activated_at 
                                                                                                        ? new Date(activation.activated_at).toLocaleDateString("ru-RU") 
                                                                                                        : "—"}
                                                                                                </span>
                                                                                                {activation.deactivated_at && (
                                                                                                    <>
                                                                                                        <span className="text-gray-300 dark:text-gray-600">→</span>
                                                                                                        <span className="text-red-500 font-medium">
                                                                                                            {new Date(activation.deactivated_at).toLocaleDateString("ru-RU")}
                                                                                                        </span>
                                                                                                    </>
                                                                                                )}
                                                                                                {!activation.deactivated_at && (
                                                                                                    <span className="ml-1 inline-block w-1.5 h-1.5 bg-green-500 rounded-full" title="Активна"></span>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    }
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap">
                                                                    {getStatusBadge(
                                                                        program.is_program_active
                                                                    )}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                    $
                                                                    {
                                                                        program.activation_price
                                                                    }
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                    $
                                                                    {formatCurrency(
                                                                        program.dividend_amount
                                                                    )}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                    {program.last_payment_at
                                                                        ? new Date(
                                                                              program.last_payment_at
                                                                          ).toLocaleDateString(
                                                                              "ru-RU"
                                                                          )
                                                                        : "-"}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                    {program.next_payment_at
                                                                        ? new Date(
                                                                              program.next_payment_at
                                                                          ).toLocaleDateString(
                                                                              "ru-RU"
                                                                          )
                                                                        : "-"}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                    {program.program.id === 1 ? (
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={program.structural_topup_paid}
                                                                            onChange={async (e) => {
                                                                                if (!selectedUserId) return;
                                                                                try {
                                                                                    await updateStructuralTopupPaid({
                                                                                        userId: selectedUserId,
                                                                                        programId: program.program.id,
                                                                                        paid: e.target.checked,
                                                                                    }).unwrap();
                                                                                } catch (error) {
                                                                                    console.error("Error updating structural topup paid:", error);
                                                                                    alert("Ошибка при обновлении статуса доплаты");
                                                                                }
                                                                            }}
                                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                                        />
                                                                    ) : (
                                                                        <span className="text-gray-400">—</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        )
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                                    Не удалось загрузить детали пользователя
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SafeStatisticsPage;
